import PlanSalesService from "@/api/services/planSales";
import { toCamelCase, isVirtualProduct } from "@estaly/ui";

const initialState = {
  index: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
};

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    setIndex(state, data) {
      state.index = data.records;
      state.currentPage = data.page.currentPage || 1;
      state.totalPages = data.page.totalPages || 0;
      state.totalCount = data.page.totalCount || 0;
    },
  },

  getters: {
    statuses: () => () => {
      return ["active", "expired", "cancelled", "suspended"];
    },
    chargedByValues: () => () => {
      return ["estaly", "merchant"];
    },
    isActive: () => (planSale) => {
      return planSale?.status?.toLowerCase() === "active";
    },
    isCancelled: () => (planSale) => {
      return planSale?.status?.toLowerCase() === "cancelled";
    },
    isExpired: () => (planSale) => {
      return planSale?.status?.toLowerCase() === "expired";
    },
    isSuspended: () => (planSale) => {
      return planSale?.status?.toLowerCase() === "suspended";
    },
    isUpdatable: () => (planSale) => {
      const status = planSale?.status?.toLowerCase();
      return status === "active" || status === "suspended";
    },
    hasFailedPayments: () => (planSale) => {
      return planSale?.planSaleBillings?.some((billing) => billing.paymentStatus === "failed") ?? false;
    },
    statusBadgeClass: () => (planSale) => {
      switch (planSale?.status) {
        case "active":
          return "bg-green-200 text-green-800";
        case "expired":
          return "bg-gray-200 text-gray-600";
        case "suspended":
          return "bg-gray-200 text-gray-600";
        case "cancelled":
          return "bg-red-200 text-red-800";
        default:
          return "bg-gray-100 text-gray-500";
      }
    },
    statusListStyle: () => (planSale) => {
      switch (planSale?.status) {
        case "cancelled":
          return "text-red-500 bg-red-100 border border-red-300";
        case "expired":
          return "text-gray-500 bg-gray-100 border border-gray-300";
        case "suspended":
          return "text-yellow-500 bg-yellow-100 border border-yellow-300";
        default:
          return "text-green-500 bg-green-100 border border-green-300";
      }
    },
    chargedByStyle: () => (planSale) => {
      return planSale?.chargedBy === "merchant"
        ? "bg-green-100 text-green-500 border border-green-300"
        : "bg-blue-100 text-blue-500 border border-blue-300";
    },
    hasVirtualProduct: () => (planSale) => {
      return planSale?.products?.some(isVirtualProduct) ?? false;
    },

    productExchangeAllowed: (_, getters, _rootState, rootGetters) => (planSale, claims) => {
      if (!getters.isActive(planSale)) return false;
      if (getters.hasVirtualProduct(planSale)) return false;
      if (claims === null || claims === undefined) return false;
      const hasActiveClaim = claims.some((claim) => rootGetters["claims/isActiveClaim"](claim));
      return !planSale.composedProductType && planSale.products?.length === 1 && !hasActiveClaim;
    },
  },
  actions: {
    async fetchIndex(
      { commit },
      {
        storeIds = [],
        organizationIds = [],
        monthly = null,
        chargedBy = [],
        status = [],
        keywords = null,
        startDate = null,
        endDate = null,
        page,
        perPage,
        includes = [],
      } = {},
    ) {
      const response = await PlanSalesService.fetchIndex({
        storeIds,
        organizationIds,
        monthly,
        chargedBy,
        status,
        keywords,
        startDate,
        endDate,
        page,
        perPage,
        includes,
        showRenewals: false,
      });
      if (!response?.success) return response;

      const data = toCamelCase(response.data);
      commit("setIndex", data);
      return data;
    },

    async fetch(_, { id, includes = [] }) {
      const response = await PlanSalesService.fetch(id, includes);
      if (!response?.success) return response;

      return toCamelCase(response.data);
    },

    updateSeller(_, { planSale, sellerId }) {
      return PlanSalesService.updateSeller(planSale.friendlyId, sellerId);
    },

    fetchPaymentMethod(_, { planSaleId }) {
      return PlanSalesService.fetchPaymentMethod(planSaleId);
    },

    fetchUpdatePaymentMethodLink(_, { planSaleId }) {
      return PlanSalesService.fetchUpdatePaymentMethodLink(planSaleId);
    },
    async cancel(_, { planSaleId, cancellationReason, feedbackMessage, cancellationDate }) {
      return await PlanSalesService.cancelPlanSale(planSaleId, cancellationReason, feedbackMessage, cancellationDate);
    },
    async reactivate(_, { planSaleId }) {
      return await PlanSalesService.reactivatePlanSale(planSaleId);
    },
    async updateDetails(_, { planSaleId, planSaleInformations }) {
      return await PlanSalesService.updatePlanSale(planSaleId, planSaleInformations);
    },
    async getBillingInvoice(_, { planSaleBillingId }) {
      const response = await PlanSalesService.getBillingInvoice(planSaleBillingId);
      if (!response?.success) return response;

      return { ...response, data: toCamelCase(response.data) };
    },
    async getRenewLink(_, { planSaleId }) {
      const response = await PlanSalesService.getRenewLink(planSaleId);
      if (!response?.success) return response;

      return toCamelCase(response.data);
    },
    async renew(_, { planSaleId }) {
      return await PlanSalesService.renewPlanSale(planSaleId);
    },
    async sendConfirmation(_, { planSaleId }) {
      return await PlanSalesService.sendConfirmationEmail(planSaleId);
    },

    async declareProductExchange(_, { friendlyId, referenceId, title, price, purchaseDate }) {
      return await PlanSalesService.exchangeProduct(friendlyId, { referenceId, title, price, purchaseDate });
    },
  },
};
